<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div
          class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3"
        >
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3">
              {{ t("students.academic_history_title") }} -
              {{ student?.full_name }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2">
        <div class="table-responsive p-2 p-md-4">
          <LoadingSpinner
            v-if="isLoading"
            :message="t('students.loading_academic_history')"
          />

          <template v-else>
            <div class="d-none d-xl-block">
              <div class="table-responsive">
                <table
                  class="table align-items-center mb-0"
                  style="width: 100%"
                >
                  <thead>
                    <tr>
                      <th
                        class="text-uppercase text-xxs font-weight-bolder opacity-7"
                        style="width: 25%"
                      >
                        {{ t("academic.year") }}
                      </th>
                      <th
                        class="text-uppercase text-xxs font-weight-bolder opacity-7"
                        style="width: 20%"
                      >
                        {{ t("students.class") }}
                      </th>
                      <th
                        class="text-uppercase text-xxs font-weight-bolder opacity-7"
                        style="width: 15%"
                      >
                        {{ t("students.group") }}
                      </th>
                      <th
                        class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                        style="width: 15%"
                      >
                        {{ t("promotion.promotion_type") }}
                      </th>
                      <th
                        class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                        style="width: 10%"
                      >
                        {{ t("common.status") }}
                      </th>
                      <th
                        class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                        style="width: 15%"
                      >
                        {{ t("common.period") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="history in academicHistory" :key="history.id">
                      <td>
                        <div class="d-flex flex-column">
                          <strong class="text-sm">{{
                            history.academic_year?.name
                          }}</strong>
                          <small class="text-xs text-muted">{{
                            history.academic_year?.year
                          }}</small>
                        </div>
                      </td>
                      <td>
                        <span class="text-sm">{{
                          history.class?.name || "N/A"
                        }}</span>
                      </td>
                      <td>
                        <span class="text-sm">{{
                          history.class_group?.group?.name || "N/A"
                        }}</span>
                      </td>
                      <td class="text-center">
                        <span
                          class="badge badge-sm"
                          :class="getPromotionTypeBadge(history.promotion_type)"
                        >
                          {{ getPromotionTypeDisplay(history.promotion_type) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <span
                          class="badge badge-sm"
                          :class="statusBadgeClass(history.status)"
                        >
                          {{ t(`students.status_${history.status}`) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <small class="text-xs">
                          {{ formatDate(history.start_date) }}
                          <span v-if="history.end_date"
                            >- {{ formatDate(history.end_date) }}</span
                          >
                        </small>
                      </td>
                    </tr>
                    <tr v-if="academicHistory.length === 0">
                      <td colspan="6" class="text-center py-4 text-muted">
                        <i class="fas fa-graduation-cap fa-2x mb-2"></i>
                        <p>{{ t("students.no_academic_history") }}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Mobile View -->
            <div class="d-xl-none">
              <div
                v-if="academicHistory.length === 0"
                class="text-center py-4 text-muted"
              >
                <i class="fas fa-graduation-cap fa-2x mb-2"></i>
                <p>{{ t("students.no_academic_history") }}</p>
              </div>

              <div v-else class="row g-3">
                <div
                  class="col-12"
                  v-for="history in academicHistory"
                  :key="history.id"
                >
                  <div class="card shadow-xs border">
                    <div class="card-body">
                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ t("academic.year") }}:</span>
                        <div class="text-end">
                          <strong class="text-sm">{{
                            history.academic_year?.name
                          }}</strong>
                          <br />
                          <small class="text-xs text-muted">{{
                            history.academic_year?.year
                          }}</small>
                        </div>
                      </div>

                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ t("students.class") }}:</span>
                        <span class="text-sm">{{
                          history.class?.name || "N/A"
                        }}</span>
                      </div>

                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ t("students.group") }}:</span>
                        <span class="text-sm">{{
                          history.class_group?.group?.name || "N/A"
                        }}</span>
                      </div>

                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm"
                          >{{ t("promotion.promotion_type") }}:</span
                        >
                        <span
                          class="badge badge-sm"
                          :class="getPromotionTypeBadge(history.promotion_type)"
                        >
                          {{ getPromotionTypeDisplay(history.promotion_type) }}
                        </span>
                      </div>

                      <div class="d-flex justify-content-between mb-2">
                        <span class="text-sm">{{ t("common.status") }}:</span>
                        <span
                          class="badge badge-sm"
                          :class="statusBadgeClass(history.status)"
                        >
                          {{ t(`students.status_${history.status}`) }}
                        </span>
                      </div>

                      <div class="d-flex justify-content-between">
                        <span class="text-sm">{{ t("common.period") }}:</span>
                        <small class="text-xs">
                          {{ formatDate(history.start_date) }}
                          <span v-if="history.end_date"
                            >- {{ formatDate(history.end_date) }}</span
                          >
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import api from "@/api";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t, locale } = useI18n();
const route = useRoute();

const isLoading = ref(false);
const student = ref(null);
const academicHistory = ref([]);

const fetchStudentAcademicHistory = async () => {
  isLoading.value = true;
  try {
    const studentId = route.params.id;

    // Fetch student details
    const studentResponse = await api.get(`/students/${studentId}`);
    student.value = studentResponse.data.data;

    // Fetch academic history
    const historyResponse = await api.get(
      `/students/${studentId}/academic-history`
    );
    academicHistory.value = historyResponse.data.data;
  } catch (error) {
    console.error("Error fetching academic history:", error);
  } finally {
    isLoading.value = false;
  }
};

const getPromotionTypeBadge = (type) => {
  const classes = {
    promote: "bg-success",
    repeat: "bg-warning",
    transfer: "bg-info",
    initial: "bg-primary",
  };
  return classes[type] || "bg-secondary";
};

const getPromotionTypeDisplay = (type, notes) => {
  if (!type) {
    // Try to infer from notes
    if (notes?.includes("ترقية") || notes?.includes("promote")) {
      return t("promotion.promote");
    } else if (notes?.includes("إعادة") || notes?.includes("repeat")) {
      return t("promotion.repeat");
    } else if (notes?.includes("نقل") || notes?.includes("transfer")) {
      return t("promotion.transfer");
    } else if (
      notes?.includes("تسجيل") ||
      notes?.includes("initial") ||
      notes?.includes("Initial")
    ) {
      return t("promotion.initial");
    } else {
      return t("promotion.promote"); // Default fallback
    }
  }
  return t(`promotion.${type}`);
};
const statusBadgeClass = (status) => {
  const classes = {
    active: "bg-success",
    completed: "bg-primary",
    transferred: "bg-warning",
    repeated: "bg-info",
  };
  return classes[status] || "bg-secondary";
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString(locale.value);
};

onMounted(() => {
  fetchStudentAcademicHistory();
});
</script>
